import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  CircularProgress,
  Collapse,
  Grid2,
  IconButton,
  Paper,
  useTheme,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { observer, useLocalObservable } from "mobx-react";
import PageTitle from "../../../../../views/components/common/pages/PageTitle";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import CustomTextField from "../../../../../views/components/common/textFields/TextField";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppSearchDropdownMulti from "../../../../../views/components/common/filter/AppSearchDropdownMulti";
import PermissionBlockLabel from "../../../../../views/components/common/pop-up/PermissionBlockLabel ";
import ConfirmationPopup from "../../../../../views/components/common/pop-up/ConfirmationPopup";
import CatalogOptionsTable from "./CatalogOptionsTable";
import CatalogOptionFormModal from "./CatalogOptionFormModal";
import { useDebounce } from "../../../../../views/components/common/hooks/Debounce";
import { useStores } from "../../../use-stores";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import { useNotification } from "../../../../../utils/NotificationsContext";
import { CatalogOptionsStore } from "../../../stores/CatalogOptionsStore";
import { FilterValue } from "../../../../../models/Filters/FilterValue";
import {
  OPTION_SORT_BY_OPTIONS,
  OptionApi,
  OptionListQuery,
  getApiErrorMessage,
  normalizeOptionSortBy,
} from "../../../../../models/CatalogOptions/types";
import { SortType } from "../../../../../models/SortType";

const SEARCH_DEBOUNCE_MS = 500;

const SORT_BY_LIST: FilterValue[] = OPTION_SORT_BY_OPTIONS.map((o) => ({
  id: o.value,
  value: o.label,
}));

const SORT_DIRECTION_LIST: FilterValue[] = [
  { id: SortType.ASC, value: "Ascending" },
  { id: SortType.DESC, value: "Descending" },
];

const ACTIVE_FILTER_LIST: FilterValue[] = [
  { id: "all", value: "All" },
  { id: "1", value: "Active" },
  { id: "0", value: "Inactive" },
];

function parseSortDirectionFromSearch(searchParams: URLSearchParams): SortType {
  const dirRaw =
    searchParams.get("sort_direction") ||
    searchParams.get("sort_order") ||
    searchParams.get("sort_type");
  const dirNorm = dirRaw?.trim().toUpperCase();
  if (!dirNorm) return SortType.ASC;
  return dirNorm === "ASC" ? SortType.ASC : SortType.DESC;
}

function parseActiveFilter(searchParams: URLSearchParams): 0 | 1 | undefined {
  const v = searchParams.get("active");
  if (v === "1" || v === "true") return 1;
  if (v === "0" || v === "false") return 0;
  return undefined;
}

function getParamsFromSearch(search: string): OptionListQuery {
  const searchParams = new URLSearchParams(search);
  const pageRaw = searchParams.get("page");
  const limitRaw = searchParams.get("limit");
  return {
    page_no: pageRaw ? parseInt(pageRaw, 10) || 1 : 1,
    limit: limitRaw ? parseInt(limitRaw, 10) || 20 : 20,
    sort_by: normalizeOptionSortBy(searchParams.get("sort_by")),
    sort_type: parseSortDirectionFromSearch(searchParams),
    search: searchParams.get("search") || undefined,
    active: parseActiveFilter(searchParams),
  };
}

const CatalogOptionsListing: React.FC = () => {
  const theme = useTheme();
  const { userStore } = useStores();
  const { showError, showSuccess } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const catalogOptionsStore = useRef(new CatalogOptionsStore()).current;

  const canView = userStore.hasPermission([
    EmployeeRoles.VIEW_CATALOG,
    EmployeeRoles.EDIT_CATALOG,
  ]);
  const canEdit = userStore.hasPermission(EmployeeRoles.EDIT_CATALOG);

  const initialParams = getParamsFromSearch(location.search);
  const [searchTerm, setSearchTerm] = useState(initialParams.search || "");
  const debouncedSearchTerm = useDebounce(searchTerm, SEARCH_DEBOUNCE_MS);

  const listing = useLocalObservable(() => {
    return {
      params: initialParams,
      isFiltersExpanded: true,
      isLoading: false,
      createModalOpen: false,
      deletePopupOpen: false,
      deletingOption: null as OptionApi | null,
      deleting: false,
      setParams(updater: (prev: OptionListQuery) => OptionListQuery) {
        this.params = updater(this.params);
      },
      syncFromLocationSearch(search: string) {
        const next = getParamsFromSearch(search);
        const prev = this.params;
        if (
          prev.page_no === next.page_no &&
          (prev.limit ?? 20) === (next.limit ?? 20) &&
          normalizeOptionSortBy(prev.sort_by) === next.sort_by &&
          (prev.sort_type ?? SortType.ASC) ===
            (next.sort_type ?? SortType.ASC) &&
          (prev.search ?? "") === (next.search ?? "") &&
          prev.active === next.active
        ) {
          return null;
        }
        this.params = next;
        return next.search ?? "";
      },
    };
  });

  // Debounced search → update list query (triggers API via params effect)
  useEffect(() => {
    const nextSearch = debouncedSearchTerm.trim() || undefined;
    if ((listing.params.search ?? "") === (nextSearch ?? "")) return;
    listing.setParams((prev) => ({
      ...prev,
      search: nextSearch,
      page_no: 1,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]);

  const refreshListing = () => {
    catalogOptionsStore.listOptions(listing.params).catch((err: unknown) => {
      showError(
        "Error",
        getApiErrorMessage(err, "Failed to load catalog options."),
      );
    });
  };

  useEffect(() => {
    if (!canView) return;
    let cancelled = false;
    listing.isLoading = true;
    catalogOptionsStore
      .listOptions(listing.params)
      .catch((err: unknown) => {
        if (cancelled) return;
        showError(
          "Error",
          getApiErrorMessage(err, "Failed to load catalog options."),
        );
      })
      .finally(() => {
        if (!cancelled) listing.isLoading = false;
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listing.params, catalogOptionsStore, canView]);

  useLayoutEffect(() => {
    if (!canView) return;
    const q = new URLSearchParams();
    q.set("page", String(listing.params.page_no ?? 1));
    if (listing.params.limit) q.set("limit", String(listing.params.limit));
    q.set("sort_by", normalizeOptionSortBy(listing.params.sort_by));
    q.set(
      "sort_direction",
      listing.params.sort_type === SortType.ASC ? "ASC" : "DESC",
    );
    if (listing.params.search) q.set("search", listing.params.search);
    if (listing.params.active !== undefined)
      q.set("active", String(listing.params.active));
    else q.set("active", String(ACTIVE_FILTER_LIST[1].id));
    navigate({ search: q.toString() }, { replace: true });
  }, [listing.params, navigate, canView]);

  useEffect(() => {
    const syncedSearch = listing.syncFromLocationSearch(location.search);
    // Only sync the input from the URL when typing has settled (debounce caught up),
    // so mid-typing values are not overwritten by a previous debounced URL update.
    if (
      syncedSearch !== null &&
      debouncedSearchTerm === searchTerm &&
      syncedSearch !== searchTerm
    ) {
      setSearchTerm(syncedSearch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const applySearchNow = (raw: string) => {
    const trimmed = raw.trim();
    setSearchTerm(raw);
    listing.setParams((prev) => ({
      ...prev,
      search: trimmed || undefined,
      page_no: 1,
    }));
  };

  const handleSearchSubmit = () => {
    applySearchNow(searchTerm);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleSearchSubmit();
  };

  const resetFilters = () => {
    setSearchTerm("");
    listing.params = {
      page_no: 1,
      limit: 20,
      sort_by: "priority",
      sort_type: SortType.ASC,
      search: undefined,
      active: undefined,
    };
  };

  const handleDeleteConfirm = async () => {
    if (!canEdit) {
      showError(
        "Permission denied",
        "You don't have permission to delete catalog options.",
      );
      return;
    }
    if (!listing.deletingOption || listing.deleting) return;
    try {
      listing.deleting = true;
      await catalogOptionsStore.deleteOption(listing.deletingOption.id);
      showSuccess(
        `Option "${listing.deletingOption.name}" soft-deleted successfully.`,
      );
      listing.deletePopupOpen = false;
      listing.deletingOption = null;
      refreshListing();
    } catch (error) {
      showError("Error", getApiErrorMessage(error, "Failed to delete option."));
    } finally {
      listing.deleting = false;
    }
  };

  if (!canView) {
    return (
      <Box sx={{ mt: 3 }}>
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <CustomTypography variant="heading3">
            Catalog Options
          </CustomTypography>
          <PermissionBlockLabel />
        </Paper>
      </Box>
    );
  }

  return (
    <Grid2
      size={{ xs: 12, sm: 12, md: 12, lg: 12 }}
      sx={{ width: "100%", maxWidth: "100%", minWidth: 0 }}
    >
      <Paper sx={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}>
        <Box
          sx={{
            marginTop: "0.6rem",
            paddingX: 4,
            paddingY: 2,
            boxShadow:
              "rgba(0, 0, 0, 0.25) 0px 0.0625em 0.0625em, rgba(0, 0, 0, 0.25) 0px 0.125em 0.5em, rgba(255, 255, 255, 0.1) 0px 0px 0px 1px inset",
            borderRadius: "4px",
            width: "100%",
            maxWidth: "100%",
            overflow: "hidden",
            minHeight: {
              xs: "60vh",
              md:
                typeof window !== "undefined"
                  ? window.innerHeight - 110
                  : "70vh",
            },
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            <Grid2
              container
              spacing={2}
              columns={12}
              alignItems="center"
              sx={{ paddingBottom: 2 }}
            >
              <Grid2 size={{ xs: 12, sm: "grow" }}>
                <PageTitle title="Catalog Options" />
              </Grid2>
              {canEdit ? (
                <Grid2 size={{ xs: 12, sm: "auto" }}>
                  <AppButton
                    variant="primary"
                    label="Create Option"
                    onClick={() => {
                      listing.createModalOpen = true;
                    }}
                  />
                </Grid2>
              ) : null}
            </Grid2>

            <Paper
              variant="outlined"
              sx={{
                mb: 2,
                borderRadius: 2,
                borderColor: "divider",
                overflow: "hidden",
              }}
            >
              <Box
                onClick={() =>
                  (listing.isFiltersExpanded = !listing.isFiltersExpanded)
                }
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  px: 2,
                  py: 0.5,
                  cursor: "pointer",
                  borderBottom: listing.isFiltersExpanded
                    ? "1px solid"
                    : "none",
                  borderColor: "divider",
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                <CustomTypography variant="heading7">
                  Search & Filters
                </CustomTypography>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    listing.isFiltersExpanded = !listing.isFiltersExpanded;
                  }}
                >
                  {listing.isFiltersExpanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>
              <Collapse
                in={listing.isFiltersExpanded}
                timeout="auto"
                unmountOnExit
              >
                <Grid2 container spacing={2} columns={12} padding={2}>
                  <Grid2 size={{ xs: 12, sm: 8, md: 8, lg: 8 }}>
                    <CustomTextField
                      width="100%"
                      placeholder="Search by name or slug"
                      value={searchTerm}
                      onSearch={(value) => {
                        setSearchTerm(value || "");
                      }}
                      onChange={(e) => {
                        setSearchTerm(e.target.value || "");
                      }}
                      endAdornment={
                        <SearchIcon
                          onClick={() => handleSearchSubmit()}
                          sx={{ color: "#7C7A7F", fontSize: "20px" }}
                        />
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleKeyDown(e);
                        }
                      }}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 6, sm: 2, md: 2, lg: 2 }}>
                    <AppButton
                      variant="primary"
                      label="Search"
                      onClick={handleSearchSubmit}
                      customStyles={{ width: "100%", height: 40 }}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 6, sm: 2, md: 2, lg: 2 }}>
                    <AppButton
                      variant="transparentPrimary"
                      label="Clear"
                      onClick={resetFilters}
                      customStyles={{ width: "100%", height: 40 }}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                    <AppSearchDropdownMulti
                      title="Sort by"
                      isMultiSelect={false}
                      local
                      list={SORT_BY_LIST}
                      value={SORT_BY_LIST.filter(
                        (o) =>
                          o.id ===
                          normalizeOptionSortBy(listing.params.sort_by),
                      )}
                      handleSelect={(items) => {
                        const next = items[0]?.id;
                        if (!next) return;
                        listing.setParams((p) => ({
                          ...p,
                          sort_by: normalizeOptionSortBy(next),
                          page_no: 1,
                        }));
                      }}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                    <AppSearchDropdownMulti
                      title="Direction"
                      isMultiSelect={false}
                      local
                      list={SORT_DIRECTION_LIST}
                      value={SORT_DIRECTION_LIST.filter(
                        (o) =>
                          o.id === (listing.params.sort_type ?? SortType.ASC),
                      )}
                      handleSelect={(items) => {
                        const next = items[0]?.id;
                        if (!next) return;
                        listing.setParams((p) => ({
                          ...p,
                          sort_type:
                            next === SortType.DESC
                              ? SortType.DESC
                              : SortType.ASC,
                          page_no: 1,
                        }));
                      }}
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                    <AppSearchDropdownMulti
                      title="Active"
                      isMultiSelect={false}
                      local
                      list={ACTIVE_FILTER_LIST}
                      value={ACTIVE_FILTER_LIST.filter((o) => {
                        if (listing.params.active === undefined) {
                          return o.id === "1";
                        }
                        return o.id === String(listing.params.active);
                      })}
                      handleSelect={(items) => {
                        const next = items[0]?.id ?? "all";
                        listing.setParams((p) => ({
                          ...p,
                          active:
                            next === "all"
                              ? undefined
                              : (Number(next) as 0 | 1),
                          page_no: 1,
                        }));
                      }}
                    />
                  </Grid2>
                </Grid2>
              </Collapse>
            </Paper>

            {listing.isLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: 280,
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <CatalogOptionsTable
                maxHeight={
                  typeof window !== "undefined" ? window.innerHeight - 360 : 480
                }
                options={catalogOptionsStore.options}
                limit={listing.params.limit ?? 20}
                page_no={listing.params.page_no ?? 1}
                total_count={catalogOptionsStore.total_count}
                sortBy={normalizeOptionSortBy(listing.params.sort_by)}
                sortType={listing.params.sort_type ?? SortType.ASC}
                onPageChange={(_e, page) =>
                  listing.setParams((p) => ({ ...p, page_no: page }))
                }
                onRowsPerPageChange={(limit) =>
                  listing.setParams((p) => ({
                    ...p,
                    limit,
                    page_no: 1,
                  }))
                }
                onSortChange={(column, sortType) =>
                  listing.setParams((p) => ({
                    ...p,
                    sort_by: normalizeOptionSortBy(column),
                    sort_type: sortType,
                    page_no: 1,
                  }))
                }
                onEdit={(option) => {
                  navigate(`/catalog-options/${option.id}`);
                }}
                onDelete={(option) => {
                  if (!canEdit) {
                    showError(
                      "Permission denied",
                      "You don't have permission to delete catalog options.",
                    );
                    return;
                  }
                  listing.deletingOption = option;
                  listing.deletePopupOpen = true;
                }}
              />
            )}
          </Box>
        </Box>
      </Paper>

      <CatalogOptionFormModal
        open={listing.createModalOpen}
        mode="create"
        store={catalogOptionsStore}
        onClose={() => {
          listing.createModalOpen = false;
        }}
        onSuccess={(optionId) => {
          if (optionId) {
            navigate(`/catalog-options/${optionId}`);
            return;
          }
          refreshListing();
        }}
      />

      {listing.deletePopupOpen && listing.deletingOption ? (
        <ConfirmationPopup
          id={listing.deletingOption.id}
          open={listing.deletePopupOpen}
          title="Delete Option"
          content={`Soft-delete "${listing.deletingOption.name}"? Its values will also be deactivated.`}
          onConfirm={() => {
            void handleDeleteConfirm();
          }}
          onCancel={() => {
            listing.deletePopupOpen = false;
            listing.deletingOption = null;
          }}
        />
      ) : null}
    </Grid2>
  );
};

export default observer(CatalogOptionsListing);
